/**
 * Credit ledger.
 *
 * This is the billing record, so it is built to be argued with: every entry
 * shows when, what for, who, how many and the balance it left behind.
 *
 * WHY THE WHOLE LEDGER IS LOADED
 * The API pages the ledger newest-first and offers no filters, and it does not
 * return a running balance. A filter applied to one page of 25 would silently
 * ignore everything else, and a running balance needs every newer entry. So the
 * ledger is fetched in full (100 per request, up to LEDGER_LOAD_CAP entries)
 * and paged, filtered and summed here. If there are more entries than the cap,
 * the screen says so rather than presenting the loaded part as the whole.
 *
 * THE RUNNING BALANCE
 * The balance after an entry = the balance at the newest end minus everything
 * newer than it. When the whole ledger is loaded, the newest-end balance is the
 * sum of the entries, and it is compared with the server's balance: a mismatch
 * is shown, not hidden. When the ledger is partial the server's balance is the
 * anchor instead, and if that is unavailable the column shows a dash rather
 * than a guess.
 *
 * ERRORS
 * A failed load renders an error with a retry — never an empty ledger. In a
 * billing screen, "no charges" and "could not load the charges" must never look
 * the same.
 */

import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { useApiError } from '../lib/useApiError';
import { Card, CardHeader, Button, Pill, EmptyState, Skeleton, ErrorNotice } from './ui';
import { IconCredits } from './icons';
import {
  creditKeys, useCreditsT,
  type ChannelOrganization, type LedgerEntry,
} from './creditsShared';

const FETCH_PAGE_SIZE = 100;
/** 20 requests' worth. Beyond this the screen says the ledger is partial. */
const LEDGER_LOAD_CAP = 2000;
const VIEW_PAGE_SIZE = 25;
const REASONS = [
  'GRANT', 'FRAMEWORK_LICENSE', 'TPRM_ASSESSMENT', 'MARITIME_ASSESSMENT',
  'VULNERABILITY_SCAN', 'SELF_ASSESSMENT_SCAN', 'TRANSFER', 'FORFEIT',
] as const;

interface LedgerPage {
  items: LedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FullLedger {
  items: LedgerEntry[];
  /** The server's count when loading began. */
  total: number;
  /** Every entry is here, and the count agrees with the server's. */
  complete: boolean;
  /** More entries exist than we are willing to load. */
  capped: boolean;
  /** Fewer entries arrived than the server announced: it changed under us. */
  shifted: boolean;
}

async function fetchFullLedger(): Promise<FullLedger> {
  const get = (page: number) =>
    api.get<LedgerPage>(`/credits/ledger${qs({ page, pageSize: FETCH_PAGE_SIZE })}`);

  const first = await get(1);
  const wanted = Math.min(first.total, LEDGER_LOAD_CAP);
  const pageCount = Math.ceil(wanted / FETCH_PAGE_SIZE);

  const pages: LedgerPage[] = [first];
  // Batches of five: fast, but not a burst the rate limiter would notice.
  for (let start = 2; start <= pageCount; start += 5) {
    const batch: Promise<LedgerPage>[] = [];
    for (let p = start; p < start + 5 && p <= pageCount; p++) batch.push(get(p));
    pages.push(...(await Promise.all(batch)));
  }

  // Dedupe by id: an entry written mid-load pushes one row onto the next page.
  const seen = new Set<string>();
  const items: LedgerEntry[] = [];
  for (const page of pages) {
    for (const entry of page.items) {
      if (seen.has(entry.id)) continue;
      seen.add(entry.id);
      items.push(entry);
    }
  }
  // Stable sort: equal timestamps keep the order the server gave them.
  items.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

  const capped = first.total > LEDGER_LOAD_CAP;
  const shifted = !capped && items.length !== first.total;
  return { items, total: first.total, complete: !capped && !shifted, capped, shifted };
}

/** Everything the table needs about one entry, worked out once. */
interface Row {
  entry: LedgerEntry;
  /** Null when it cannot be stated honestly. */
  balanceAfter: number | null;
}

function startOfDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 0, 0, 0, 0).getTime();
}
function endOfDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 23, 59, 59, 999).getTime();
}

interface Filters { reason: string; direction: '' | 'in' | 'out'; from: string; to: string; q: string }
const NO_FILTERS: Filters = { reason: '', direction: '', from: '', to: '', q: '' };

export function CreditLedger({ balance, channel }: {
  /** The server's balance, or undefined while loading or after a failure. */
  balance: number | undefined;
  /** Used only to name the other side of a transfer. */
  channel: ChannelOrganization[] | undefined;
}) {
  const { tx, tc, reason: reasonLabel, num, signed, locale } = useCreditsT();
  const describeError = useApiError();
  const { session } = useAuth();
  const organizationId = session?.organization.id ?? '';

  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [page, setPage] = useState(1);

  const set = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const ledger = useQuery({
    queryKey: creditKeys(organizationId).ledger,
    queryFn: fetchFullLedger,
    placeholderData: keepPreviousData,
  });
  // A failed refresh must not leave last minute's rows looking current.
  const data = ledger.isError ? undefined : ledger.data;

  const orgName = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of channel ?? []) map.set(o.id, o.name);
    return map;
  }, [channel]);

  // --- Running balance -------------------------------------------------------

  const ledgerSum = useMemo(
    () => (data ? data.items.reduce((sum, e) => sum + e.delta, 0) : 0),
    [data],
  );

  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    const anchor = data.complete ? ledgerSum : balance;
    let running = anchor;
    return data.items.map((entry) => {
      const balanceAfter = running ?? null;
      if (running !== undefined) running -= entry.delta;
      return { entry, balanceAfter };
    });
  }, [data, ledgerSum, balance]);

  const mismatch = data?.complete && balance !== undefined && ledgerSum !== balance;

  // --- Filtering -------------------------------------------------------------

  const rangeInverted = !!filters.from && !!filters.to && filters.from > filters.to;
  const hasFilters = Object.values(filters).some(Boolean);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const from = filters.from ? startOfDay(filters.from) : null;
    const to = filters.to ? endOfDay(filters.to) : null;
    return rows.filter(({ entry }) => {
      if (filters.reason && entry.reason !== filters.reason) return false;
      if (filters.direction === 'in' && entry.delta <= 0) return false;
      if (filters.direction === 'out' && entry.delta >= 0) return false;
      const at = Date.parse(entry.createdAt);
      if (from !== null && at < from) return false;
      if (to !== null && at > to) return false;
      if (q) {
        const haystack = [
          entry.note, entry.actorName, entry.referenceId, entry.reason,
          entry.referenceId ? orgName.get(entry.referenceId) : null,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters, orgName]);

  const totals = useMemo(() => {
    let inSum = 0; let outSum = 0;
    for (const { entry } of filtered) {
      if (entry.delta > 0) inSum += entry.delta; else outSum += entry.delta;
    }
    return { inSum, outSum, net: inSum + outSum };
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / VIEW_PAGE_SIZE));
  // A reload can shrink the list under the current page.
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const visible = filtered.slice((page - 1) * VIEW_PAGE_SIZE, page * VIEW_PAGE_SIZE);

  const when = useMemo(() => {
    const f = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
    return (iso: string) => f.format(new Date(iso));
  }, [locale]);

  // --- Text for one entry ----------------------------------------------------

  const whoLabel = (e: LedgerEntry) =>
    e.actorName ?? (e.actorId ? tx('credits.ledger.unnamedUser') : tx('credits.ledger.system'));

  /**
   * Several charge paths record who acted as an id only, with no name. The id
   * tail is shown so two such entries can at least be told apart and matched to
   * the audit trail; the full id is in the tooltip.
   */
  const whoId = (e: LedgerEntry) =>
    !e.actorName && e.actorId ? (
      <span className="block text-xs text-ink-muted font-mono" title={e.actorId}>…{e.actorId.slice(-6)}</span>
    ) : null;

  /** The line under the reason: the counterparty, note and reference. */
  const detailOf = (e: LedgerEntry): { lines: string[]; ref: string | null } => {
    const lines: string[] = [];
    let ref: string | null = null;

    if (e.reason === 'TRANSFER') {
      const other = e.referenceId ? orgName.get(e.referenceId) : undefined;
      const name = other ?? tx('credits.ledger.otherOrg');
      lines.push(e.delta < 0 ? tx('credits.ledger.toOrg', { name }) : tx('credits.ledger.fromOrg', { name }));
    } else if (e.referenceId) {
      ref = tx('credits.ledger.ref', { id: e.referenceId });
    }

    // The server writes "Transfer to <organisation id>" when no note is given.
    // That is a raw id, not a note, and the counterparty line already says it.
    const defaultTransferNote = e.referenceId ? `Transfer to ${e.referenceId}` : null;
    if (e.note && e.note !== defaultTransferNote) lines.push(e.note);

    return { lines, ref };
  };

  // --- Render ----------------------------------------------------------------

  const balanceCell = (r: Row) =>
    r.balanceAfter === null ? <span className="text-ink-muted">—</span> : num(r.balanceAfter);

  return (
    <Card>
      <CardHeader title={tx('credits.ledger.title')} subtitle={tx('credits.ledger.subtitle')} />

      {ledger.isPending ? (
        <div className="space-y-2" aria-busy>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : ledger.isError ? (
        <ErrorNotice
          message={`${tx('credits.ledger.loadFailed')} ${describeError(ledger.error)}`}
          onRetry={() => void ledger.refetch()}
          retryLabel={tc('common.retry')}
        />
      ) : !data ? null : (
        <div className="space-y-4">
          {data.capped && (
            <ErrorNotice
              message={tx('credits.ledger.partial', { loaded: num(data.items.length), total: num(data.total) })}
            />
          )}
          {data.shifted && (
            <ErrorNotice message={tx('credits.ledger.changed')} onRetry={() => void ledger.refetch()}
                         retryLabel={tx('credits.ledger.reload')} />
          )}
          {mismatch && (
            <ErrorNotice
              message={tx('credits.ledger.mismatch', { ledger: num(ledgerSum), balance: num(balance!) })}
              onRetry={() => void ledger.refetch()} retryLabel={tx('credits.ledger.reload')}
            />
          )}

          {data.items.length === 0 ? (
            !data.capped && !data.shifted && (
              <EmptyState
                icon={<IconCredits className="w-10 h-10" />}
                title={tx('credits.ledger.none')}
                description={tx('credits.ledger.noneHint')}
              />
            )
          ) : (
            <>
              {/* Filters */}
              <div className="rounded-xl border border-line p-3">
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                  <label className="block">
                    <span className="oc-label">{tx('credits.ledger.filterType')}</span>
                    <select className="oc-input" value={filters.reason}
                            onChange={(e) => set({ reason: e.target.value })}>
                      <option value="">{tc('common.all')}</option>
                      {REASONS.map((r) => <option key={r} value={r}>{reasonLabel(r)}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="oc-label">{tx('credits.ledger.filterDirection')}</span>
                    <select className="oc-input" value={filters.direction}
                            onChange={(e) => set({ direction: e.target.value as Filters['direction'] })}>
                      <option value="">{tc('common.all')}</option>
                      <option value="in">{tx('credits.ledger.directionIn')}</option>
                      <option value="out">{tx('credits.ledger.directionOut')}</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="oc-label">{tx('credits.ledger.from')}</span>
                    <input className="oc-input" type="date" value={filters.from}
                           max={filters.to || undefined}
                           onChange={(e) => set({ from: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="oc-label">{tx('credits.ledger.to')}</span>
                    <input className="oc-input" type="date" value={filters.to}
                           min={filters.from || undefined}
                           onChange={(e) => set({ to: e.target.value })} />
                  </label>
                  <label className="block col-span-2 lg:col-span-1">
                    <span className="oc-label">{tx('credits.ledger.search')}</span>
                    <input className="oc-input" type="search" value={filters.q}
                           placeholder={tx('credits.ledger.searchPlaceholder')}
                           onChange={(e) => set({ q: e.target.value })} />
                  </label>
                </div>
                {hasFilters && (
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { setFilters(NO_FILTERS); setPage(1); }}>
                      {tc('common.clearFilters')}
                    </Button>
                  </div>
                )}
              </div>

              {rangeInverted && <ErrorNotice message={tx('credits.ledger.rangeInverted')} />}

              {/* Totals for what the filters currently show */}
              {filtered.length > 0 && (
                <div>
                  <dl className="grid grid-cols-3 gap-2 text-sm">
                    <Total label={tx('credits.ledger.totalIn')} value={signed(totals.inSum)} tone="good" />
                    <Total label={tx('credits.ledger.totalOut')} value={signed(totals.outSum)} />
                    <Total label={tx('credits.ledger.totalNet')} value={signed(totals.net)} />
                  </dl>
                  <p className="mt-1 text-xs text-ink-muted">
                    {tx('credits.ledger.totalsNote', { count: filtered.length })}
                  </p>
                </div>
              )}

              {filtered.length === 0 ? (
                <EmptyState
                  icon={<IconCredits className="w-10 h-10" />}
                  title={tx('credits.ledger.noMatch')}
                  action={<Button onClick={() => { setFilters(NO_FILTERS); setPage(1); }}>
                    {tc('common.clearFilters')}
                  </Button>}
                />
              ) : (
                <>
                  {/* Desktop: a table, because the point is comparing numbers down a column. */}
                  <div className="oc-table-scroll hidden md:block">
                    <table className="w-full text-sm">
                      <caption className="sr-only">{tx('credits.ledger.caption')}</caption>
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
                          <th scope="col" className="font-medium py-2 pr-3 whitespace-nowrap">{tx('credits.ledger.when')}</th>
                          <th scope="col" className="font-medium py-2 pr-3">{tx('credits.ledger.what')}</th>
                          <th scope="col" className="font-medium py-2 pr-3">{tx('credits.ledger.who')}</th>
                          <th scope="col" className="font-medium py-2 pr-3 text-right">{tx('credits.ledger.amount')}</th>
                          <th scope="col" className="font-medium py-2 text-right" title={tx('credits.ledger.balanceAfterHint')}>
                            {tx('credits.ledger.balanceAfter')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((r) => {
                          const { entry } = r;
                          const d = detailOf(entry);
                          return (
                            <tr key={entry.id} className="border-b border-line/60 align-top"
                                title={tx('credits.ledger.entryId', { id: entry.id })}>
                              <td className="py-2.5 pr-3 whitespace-nowrap">
                                <time dateTime={entry.createdAt} title={entry.createdAt} className="tabular-nums">
                                  {when(entry.createdAt)}
                                </time>
                              </td>
                              <td className="py-2.5 pr-3 min-w-0">
                                <Pill tone={entry.reason === 'FORFEIT' ? 'warning' : 'neutral'}>
                                  {reasonLabel(entry.reason)}
                                </Pill>
                                <Detail lines={d.lines} refText={d.ref} />
                              </td>
                              <td className="py-2.5 pr-3 text-ink-secondary">{whoLabel(entry)}{whoId(entry)}</td>
                              <td className={cn('py-2.5 pr-3 text-right tabular-nums font-medium whitespace-nowrap',
                                entry.delta > 0 ? 'text-good' : 'text-ink')}>
                                <span className="sr-only">
                                  {entry.delta > 0 ? tx('credits.ledger.credited') : tx('credits.ledger.debited')}{' '}
                                </span>
                                {signed(entry.delta)}
                              </td>
                              <td className="py-2.5 text-right tabular-nums text-ink whitespace-nowrap">
                                {balanceCell(r)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Phone: one stacked card per entry. */}
                  <ul className="md:hidden divide-y divide-line/60 -mx-1" aria-label={tx('credits.ledger.caption')}>
                    {visible.map((r) => {
                      const { entry } = r;
                      const d = detailOf(entry);
                      return (
                        <li key={entry.id} className="py-3 px-1" title={tx('credits.ledger.entryId', { id: entry.id })}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <Pill tone={entry.reason === 'FORFEIT' ? 'warning' : 'neutral'}>
                                {reasonLabel(entry.reason)}
                              </Pill>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={cn('tabular-nums font-semibold', entry.delta > 0 ? 'text-good' : 'text-ink')}>
                                <span className="sr-only">
                                  {entry.delta > 0 ? tx('credits.ledger.credited') : tx('credits.ledger.debited')}{' '}
                                </span>
                                {signed(entry.delta)}
                              </p>
                              <p className="text-xs text-ink-muted tabular-nums">
                                <span className="sr-only">{tx('credits.ledger.balanceAfterHint')}: </span>
                                {balanceCell(r)}
                              </p>
                            </div>
                          </div>
                          <Detail lines={d.lines} refText={d.ref} />
                          <p className="mt-1 text-xs text-ink-secondary">
                            <time dateTime={entry.createdAt} title={entry.createdAt} className="tabular-nums">
                              {when(entry.createdAt)}
                            </time>
                            {' · '}{whoLabel(entry)}
                            {!entry.actorName && entry.actorId && (
                              <span className="font-mono text-ink-muted" title={entry.actorId}> …{entry.actorId.slice(-6)}</span>
                            )}
                          </p>
                        </li>
                      );
                    })}
                  </ul>

                  <nav className="flex flex-wrap items-center justify-between gap-2"
                       aria-label={tx('credits.ledger.pagination')}>
                    <p className="oc-muted tabular-nums">
                      {hasFilters
                        ? tx('credits.ledger.showingFiltered', {
                            from: num((page - 1) * VIEW_PAGE_SIZE + 1),
                            to: num((page - 1) * VIEW_PAGE_SIZE + visible.length),
                            total: num(filtered.length), all: num(rows.length),
                          })
                        : tx('credits.ledger.showing', {
                            from: num((page - 1) * VIEW_PAGE_SIZE + 1),
                            to: num((page - 1) * VIEW_PAGE_SIZE + visible.length),
                            total: num(filtered.length),
                          })}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        {tc('common.previous')}
                      </Button>
                      <Button size="sm" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
                        {tc('common.next')}
                      </Button>
                    </div>
                  </nav>
                </>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Detail({ lines, refText }: { lines: string[]; refText: string | null }) {
  if (lines.length === 0 && !refText) return null;
  return (
    <div className="mt-1 space-y-0.5 min-w-0">
      {lines.map((line, i) => (
        // `note` is free text written by a person or the server: rendered as a
        // text node only, and allowed to wrap rather than stretch the table.
        <p key={i} className="text-sm text-ink break-words">{line}</p>
      ))}
      {refText && <p className="text-xs text-ink-muted font-mono break-all">{refText}</p>}
    </div>
  );
}

function Total({ label, value, tone }: { label: string; value: string; tone?: 'good' }) {
  return (
    <div className="rounded-lg bg-ink/[0.04] px-3 py-2">
      <dt className="text-xs text-ink-muted uppercase tracking-wide">{label}</dt>
      <dd className={cn('tabular-nums font-semibold', tone === 'good' ? 'text-good' : 'text-ink')}>{value}</dd>
    </div>
  );
}
